import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AnalyticsService } from '../../@core/utils';
import { STUDENT_EXTRA_HELP_MODES } from '../../enum/student-extra-help';
import { Classroom } from '../../interface/calendar';
import { User, UserData } from '../../interface/user';
import { AssignmentEvaluationService } from '../../services/assignment-evaluation.service';
import { AssignmentsService } from '../../services/assignments.service';
import { AuthService } from '../../services/auth.service';
import { NotificationsService } from '../../services/notifications.service';
import { OpenAIService } from '../../services/open-ai.service';
import { PacingPlanService } from '../../services/pacing-plan.service';
import { StorageService } from '../../services/storage.service';
import { StudentExtraHelpService } from '../../services/student-extra-help.service';
import { StudentService } from '../../services/student.service';
import { ToasterService } from '../../services/toaster.service';
import { UtilService } from '../../services/util.service';
import { MatDialogComponent } from '../mat-dialog/mat-dialog.component';

@Component({
  selector: 'ngx-students-extra-help',
  templateUrl: './students-extra-help.component.html',
  styleUrls: ['./students-extra-help.component.scss'],
})
export class StudentsExtraHelpComponent implements OnInit, OnDestroy {
  @Input() isExpand = false;
  @Input() classroom;
  @Input() currentUser: User;
  extraHelpMode: string;
  generateQuestionInputValue;
  isLoading = false;
  searchUsersText = '';
  explainSolution = '';
  user: UserData;
  studentNotes = [];
  selectedNote;
  selectedQuestion;
  bulkMessage = '';
  studentQuestions = [];
  @ViewChild('dialogHelp') dialogHelp: TemplateRef<any>;
  userAccessToken: any;
  userAccessAIToken: any;
  studentPreviouslyAskedQuestions = [];
  isHistoryOpen: boolean = false;
  public searchText: string = '';
  @ViewChild('dialogDelete') dialogDelete: TemplateRef<any>;
  private currentDeleteId: number;
  isEditing = false;
  upcomingAssignments = [];
  @Input() selectedOption: any;
  @Output() selectedOptionChange = new EventEmitter<any>();
  isComplete$ = new BehaviorSubject<boolean>(false);
  showAskedQuestion: boolean = false;
  showAIAskedQuestion: boolean = false;
  isStreamingCompleted = false;
  @Input() selectedQuestionFromList: any;
  studentExtraHelpMode = STUDENT_EXTRA_HELP_MODES;
  buttonDisabled: boolean = false;

  private classroomSubscription: Subscription;
  private extrahelpOptionChangeSubscription: Subscription;
  constructor(
    private studentService: StudentService,
    private openAiService: OpenAIService,
    public storageService: StorageService,
    private assignmentService: AssignmentsService,
    private evaluationService: AssignmentEvaluationService,
    private notificationService: NotificationsService,
    private authService: AuthService,
    public dialog: MatDialog,
    public router: Router,
    public analyticsService: AnalyticsService,
    private pacingPlanService: PacingPlanService,
    private toasterService: ToasterService,
    public dialogRef: MatDialogRef<any>,
    public spinner: NgxSpinnerService,
    public utilService: UtilService,
    private extraHelpService: StudentExtraHelpService,
  ) {}

  async ngOnInit() {
    try {
      this.classroomSubscription = this.studentService.currentClassroom.subscribe(async (x: Classroom) => {
        if (x) {
          this.spinner.show();
          this.classroom = x;
          await this.getStudentAssignmentsQuestions();
          // changed previously asked questions as per classroom
          await this.getPreviouslyAskedStudentQuestion();
          this.explainSolution = '';
          this.generateQuestionInputValue = '';
          this.spinner.hide();
        }
      });

      this.extrahelpOptionChangeSubscription = this.studentService.studentHelpOptionChange.subscribe(async (x: any) => {
        if (x && x.onChange) {
          this.extraHelpMode = x.mode;
          this.isHistoryOpen = true;
          await this.getPreviouslyAskedStudentQuestion();
        }
      });

      if (this.selectedOption) {
        this.extraHelpMode = this.selectedOption.mode;
        this.isHistoryOpen = true;
        await this.getPreviouslyAskedStudentQuestion();
      }

      this.user = await this.storageService.getItem('user');

      this.spinner.show();

      await this.getPreviouslyAskedStudentQuestion();
      await this.getStudentAssignmentsQuestions();
      // reassigned the values that are assigned in the card view
      if (this.isExpand) {
        this.extraHelpMode = this.extraHelpMode || '';
        // to show the history of the questions asked by the student/parent in expand view
        this.isHistoryOpen = true;
        if (this.storageService.checkUserRole(this.user?.user?.role_id, 'parent')) {
          this.extraHelpMode = STUDENT_EXTRA_HELP_MODES.Generate_Solution_From_AI;
          // get the previously asked question to the parent
          await this.getPreviouslyAskedStudentQuestion();
        }
        const match = this.studentPreviouslyAskedQuestions.find((q) => q.id === this.selectedQuestionFromList?.id);
        this.generateQuestionInputValue = match;
        this.selectedQuestion = match;
        this.explainSolution = match?.user_answer || '';
        this.bulkMessage = match?.message;
        if (this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Generate_Solution_From_AI && match) {
          this.showAIAskedQuestion = true;
        }
        if (
          (this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Teacher ||
            this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Student) &&
          match
        ) {
          this.showAskedQuestion = true;
        }
      } else {
        this.extraHelpMode = STUDENT_EXTRA_HELP_MODES.Generate_Solution_From_AI;
        // to show the history of the questions asked by the student/parent in the card view
        this.isHistoryOpen = true;
        await this.getPreviouslyAskedStudentQuestion();
      }
      this.spinner.hide();

      this.pacingPlanService.studentCurrentSubject.subscribe(async (x) => {
        this.spinner.show();

        this.classroom['current_subject_id'] = x;
        await this.getStudentAssignmentsQuestions();
        await this.getPreviouslyAskedStudentQuestion();
        this.spinner.hide();
      });

      // this.getUserToken();
      this.getUserAIToken();
    } catch (error) {
      this.spinner.hide();
      console.log('students-extra-help.component.ts:155 ~ ngOnInit ~ error:', error);
    }
  }

  checkForQuestions(type: 'assignment' | 'student-teacher') {
    const message =
      type === 'assignment' && this.upcomingAssignments?.length === 0
        ? 'No ongoing assignment found'
        : 'No questions found';

    if (this.studentQuestions.length === 0) {
      this.toasterService.showToast(message, 'warning');
    }
  }

  async getStudentAssignmentsQuestions() {
    try {
      const assignments: any = await this.assignmentService.assignmentByClassroomAndSubjectId({
        classroom_id: this.classroom.id,
        subject_id: this.classroom?.current_subject_id,
      });
      this.upcomingAssignments = assignments;
      if (assignments && assignments.length > 0) {
        const assignments_ids = assignments.map((x) => x.id);
        this.studentQuestions = await this.assignmentService.AssignmentQuestionsByAssignmentsIds({
          assignments_ids,
        });
      } else {
        this.studentQuestions = [];
      }
    } catch (error) {
      // console.log(error);
    }
  }

  closeExpandView() {
    this.studentService.isExpand.emit({
      isExpand: false,
      compontentLabel: 'notes',
    });
  }

  async changeExtraHelpMode(mode: string, isSelect = false) {
    try {
      this.spinner.show();
      this.showAskedQuestion = false;
      this.showAIAskedQuestion = false;
      this.extraHelpMode = isSelect ? mode : '';
      this.isHistoryOpen = true;
      this.selectedQuestion = null;
      this.generateQuestionInputValue = '';
      this.explainSolution = '';
      this.bulkMessage = '';
      this.checkHistory();
      !isSelect ? (this.extraHelpMode = '') : (this.extraHelpMode = mode);
      await this.getPreviouslyAskedStudentQuestion();

      this.selectedOptionChange.emit({ mode });
      this.studentService.studentHelpOptionChange.emit({
        onChange: this.isExpand ? true : false,
        mode,
      });
      this.spinner.hide();
    } catch (error) {
      this.spinner.hide();
      // console.log(error);
    }
  }

  async generateSolution(stream) {
    try {
      this.buttonDisabled = true;
      this.spinner.show();
      const body = {
        assignment_questions_id: this.generateQuestionInputValue?.id,
      };
      const solutionFromDatabse: any = await this.studentService.getAiSolutionFromDatabase(body);

      if (solutionFromDatabse && solutionFromDatabse?.items?.length > 0) {
        this.explainSolution = solutionFromDatabse?.items[0].user_answer;
        this.spinner.hide();

        // Check if the solution is from a different user and if the question was not already asked by the student, if so, add the solution in DB
        if (
          solutionFromDatabse?.items[0].from_user_id !== this.user?.user?.id &&
          !this.isQuestionAlreadyAsked(solutionFromDatabse?.items[0].question)
        ) {
          await this.addExtraHelpSolution(solutionFromDatabse?.items[0].user_answer);
        }
        await this.getPreviouslyAskedStudentQuestion();
        this.buttonDisabled = false;
      } else {
        // if (this.userAccessToken[0]?.tokens_used && this.userAccessToken[0]?.tokens_used >= 12) {

        if (this.userAccessAIToken.tokens_used && this.userAccessToken?.tokens_used >= 12) {
          this.toasterService.showToast(
            'You have exceeded token limit usage per month, please try again after a month',
            'danger',
          );
          this.spinner.hide();
        } else {
          let index = 0;

          if (!stream) {
            try {
              const response = await this.openAiService.openAIEncryptionCallForGenerateSolutions(
                this.generateQuestionInputValue?.content,
                false,
              );

              if (response && response.code === 200) {
                this.explainSolution = response?.data?.content;
                this.spinner.hide();
                await this.addExtraHelpSolution(response?.data?.content);
                await this.getPreviouslyAskedStudentQuestion();
              }
            } catch (error) {
              console.error('Error fetching solution:', error);
              this.toasterService.showToast(
                'Our AI Assistant is not working currently, Please try again after some time',
                'danger',
              );
              this.spinner.hide();
            }
          } else {
            (
              await this.openAiService.openAIEncryptionCallForGenerateSolutions(
                this.generateQuestionInputValue?.content,
                true,
              )
            ).subscribe({
              next: async (data: any) => {
                this.spinner.hide();
                this.isStreamingCompleted = true;
                this.extraHelpService.isStreaming$.emit(true);
                this.explainSolution = index === 0 ? data : this.explainSolution + data;
                index++;
              },

              error: (error) => {
                console.error('Error receiving stream:', error);
                this.toasterService.showToast(
                  'Our AI Assistant not working currently, Please try again after some time',
                  'danger',
                );
                this.spinner.hide();
              },

              complete: async () => {
                this.spinner.hide();
                this.isStreamingCompleted = false;
                this.extraHelpService.isStreaming$.emit(false);
                await this.addExtraHelpSolution(this.explainSolution);
                await this.getPreviouslyAskedStudentQuestion();
                this.buttonDisabled = false;
              },
            });

            if (
              this.storageService.checkUserRole(this.user?.user?.role_id, 'student') &&
              this.user?.user?.school?.user_limitations[0]?.isLimitedAccess === 'true'
            ) {
              await this.updateAITokenUsed();
              // await this.updateTokenUsed();
            }
          }

          if (
            this.storageService.checkUserRole(this.user?.user?.role_id, 'student') &&
            this.user?.user?.school?.user_limitations[0]?.isLimitedAccess === 'true'
          ) {
            await this.updateAITokenUsed();
            // await this.updateTokenUsed();
          }
        }
      }
    } catch (error) {
      this.spinner.hide();
      this.buttonDisabled = false;
      // console.log(error);
    }
  }

  async addExtraHelpSolution(answer) {
    await this.addExtraHelpToDatabase({
      type: 1,
      question: this.generateQuestionInputValue?.content,
      from_user_id: this.user.user.id,
      user_answer: answer,
      status: 1,
      assignment_questions_id: this.generateQuestionInputValue?.id,
    });

    await this.setPowerUpPoints(10);
  }

  async addExtraHelpToDatabase(requestParams) {
    try {
      const body = {
        question: requestParams.question ? requestParams.question : '',
        student_extra_help_type_id: requestParams.type ? requestParams.type : '',
        students_notes_id: requestParams.students_notes_id ? requestParams.students_notes_id : '',
        assignment_questions_id: requestParams.assignment_questions_id ? requestParams.assignment_questions_id : '',
        from_user_id: requestParams.from_user_id ? requestParams.from_user_id : '',
        to_user_id: requestParams.to_user_id ? requestParams.to_user_id : '',
        status: requestParams.status ? requestParams.status : '',
        user_answer: requestParams.user_answer ? requestParams.user_answer : '',
        message: requestParams.message ? requestParams.message : '',
        classroom_id: this.classroom?.id,
        from_time: new Date().getTime(),
      };

      return await this.studentService.addStudentExtraHelp(body);
    } catch (error) {
      // console.log(error);
    }
  }

  async addExtraHelp() {
    try {
      this.spinner.show();

      const students =
        this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Student ? await this.checkForStudents() : null;

      const isRequest =
        this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Student ? (students?.length > 0 ? true : false) : true;

      if (isRequest) {
        const exptraHelpParams = {
          type: this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Student ? 3 : 2,
          question: this.selectedQuestion?.content,

          assignment_questions_id: this.selectedQuestion?.id,
          from_user_id: this.user.user.id,
          to_user_id:
            this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Student ? students : [this.classroom?.user_id],
          status: 0,
          message: this.bulkMessage,
        };

        const response: any = await this.addExtraHelpToDatabase(exptraHelpParams);
        if (response && response.id) {
          this.openSubmitModal();

          this.bulkMessage = '';
          this.selectedQuestion = null;
          this.spinner.hide();

          await this.addNotification(exptraHelpParams?.to_user_id);

          await this.setPowerUpPoints(this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Student ? 20 : 5);
        }
        await this.getPreviouslyAskedStudentQuestion();
      } else {
        await this.openHelpModal();
        this.spinner.hide();
      }
    } catch (error) {
      // console.log(error);
    }
  }

  openSubmitModal(): void {
    this.dialog.open(MatDialogComponent, {
      width: '400px',
      data: {
        title: 'Welcome',
        icon: '/assets/icons/request-send.svg',
        accessCodeLabel: 'Request Sent',
        confirmationMessage: `Your request has been sent! The ${
          this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Student ? 'students' : 'teacher'
        } will be notified.`,
        confirmButtonText: 'Ok',
        trackPage: 'Student Help Request',
      },
    });
  }

  async openHelpModal() {
    try {
      this.dialog.open(this.dialogHelp, {
        width: '370px',
        height: '295px',
      });

      // const dialogRef = this.dialog.open(MatDialogComponent, {
      //   width: '400px',
      //   data: {
      //     title: 'Welcome',
      //     icon: '/assets/icons/featured icon.svg',
      //     accessCodeLabel: 'Delete User',
      //     confirmationMessage: 'Are you sure want to delete this user ?',
      //     cancelButtonText: 'Cancel',
      //     confirmButtonText: 'Ok',
      //     cancelTrackEvent: 'Cancel Delete User',
      //     confirmTrackEvent: 'Delete User',
      //     trackPage: 'User',
      //   },
      // });
      //  dialogRef.afterClosed().subscribe(async (result) => {
      //    if (result) {
      //      this.deleteUserFromLocalStorage();
      //    }
      //  });
    } catch (error) {
      // console.log(error);
    }
  }

  async addNotification(users = []) {
    try {
      if (users.length > 0) {
        const notificationsToStore = {
          notifications: [],
        };
        for (const iterator of users) {
          const body = {
            title: 'You have a new request',
            message: `You have got a new request from ${this.user?.user?.firstName} ${this.user?.user?.lastName} for help`,
            table_reference: 'request-help',
            read_status: false,
            user_id: this.user.user.id,
            recieved_to: iterator,
          };
          notificationsToStore.notifications.push(body);
        }
        await this.notificationService.addMultipleNotifications(notificationsToStore);
      }
    } catch (error) {
      // console.log(error)
    }
  }

  async checkForStudents() {
    try {
      const response: any = await this.evaluationService.getEvaluationByQuestionIdAndCorrectNess({
        assignment_questions_id: this.selectedQuestion?.id,
      });

      const studentsData = [];
      for (const iterator of response) {
        if (iterator?.user_id !== this.user?.user?.id) studentsData.push(iterator.user_id);
      }

      return studentsData;
    } catch (error) {
      // console.log(error);
    }
  }

  async setPowerUpPoints(points) {
    try {
      const response: any = await this.authService.getPowerUpPointsByUserId({
        user_id: this.user.user.id,
      });

      if (response && response.length > 0) {
        const newPoints = response[0].points;

        newPoints.push({
          points: points,
          type: points === 10 ? 1 : points === 20 ? 3 : 2,
        });

        await this.authService.updatePowerUpPoints(response[0].id, {
          user_id: response[0].user_id,
          points: newPoints,
          school_id: response[0].school_id,
          role_id: response[0].role_id,
        });
      } else {
        await this.authService.addPowerUpPoints({
          user_id: this.user.user.id,
          points: [
            {
              points: points,
              type: points === 10 ? 1 : points === 20 ? 3 : 2,
            },
          ],
          school_id: this.user.user.school_id,
          role_id: this.user.user.role_id,
        });
      }
    } catch (error) {
      // console.log(error);
    }
  }
  // async getUserToken() {
  //   try {
  //     // restrict openAI calls
  //     this.userAccessToken = await this.utilService.getUserToken(this.user?.user);
  //   } catch (error) {
  //     console.log('StudentsExtraHelpComponent getUserToken', error);
  //   }
  // }
  // async updateTokenUsed() {
  //   try {
  //     // restrict openAI calls
  //     await this.utilService.updateTokenUsed(this.user?.user, this.userAccessToken);

  //     await this.getUserToken();
  //   } catch (error) {
  //     console.log('StudentsExtraHelpComponent updateTokenUsed', error);
  //   }
  // }
  async getPreviouslyAskedStudentQuestion() {
    try {
      const student_extra_help_type_id =
        this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Generate_Solution_From_AI
          ? 1
          : this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Teacher
            ? 2
            : 3;

      const body = {
        user_id: this.user.user.id,
        classroom_id: this.classroom.id,
        student_extra_help_type_id: student_extra_help_type_id,
      };

      this.studentPreviouslyAskedQuestions = await this.studentService.getStudentPreviousQuestion(body);
      this.studentPreviouslyAskedQuestions.sort((a, b) => b.created_at - a.created_at);
    } catch (error) {
      // console.log(error);
    }
  }
  public checkHistory() {
    try {
      this.isHistoryOpen = this.extraHelpMode !== 'Default' && this.extraHelpMode !== '';
    } catch (error) {
      console.log(error);
    }
  }

  openDeleteModal(id: number) {
    try {
      this.currentDeleteId = id;
      // this.dialogRef = this.dialog.open(this.dialogDelete, {
      //   width: '365px',
      //   height: '270px',
      // });
      this.openConfirmDeleteDialog();
    } catch (error) {
      // console.log(error)
    }
  }

  public async deleteQuestion() {
    try {
      this.spinner.show();

      // Check if the question being deleted is currently selected
      const isCurrentlySelected =
        this.generateQuestionInputValue?.id === this.currentDeleteId ||
        this.selectedQuestion?.id === this.currentDeleteId;

      await this.studentService.deleteStudentPreviousQuestion(this.currentDeleteId);
      await this.getPreviouslyAskedStudentQuestion();

     // Only reset if the deleted question was the one currently selected, otherwise keep the current selection
      if (isCurrentlySelected) {
        this.generateQuestionInputValue = null;
        this.selectedQuestion = null;
        this.bulkMessage = '';
        this.explainSolution = '';
        this.isEditing = false;
        this.showAskedQuestion = false;
        this.showAIAskedQuestion = false;
      }

      this.spinner.hide();
    } catch (error) {
      this.spinner.hide();
      // console.log(error);
    }
  }
  public async showQuestionRecord(event, questionRecord) {
    try {
      if (this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Generate_Solution_From_AI) {
        this.showAIAskedQuestion = true;
      }
      if (
        this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Teacher ||
        this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Ask_Student
      ) {
        this.showAskedQuestion = true;
      }
      const foundQuestion = this.studentPreviouslyAskedQuestions.find(
        (question) => question.question === questionRecord.question,
      );
      this.generateQuestionInputValue = foundQuestion;
      this.explainSolution =
        this.extraHelpMode === STUDENT_EXTRA_HELP_MODES.Generate_Solution_From_AI
          ? questionRecord?.user_answer
          : questionRecord?.message;
      this.selectedQuestion = foundQuestion;
      // this.selectedQuestionFromList = this.selectedQuestion;
      this.extraHelpService.selectedQuestion.next({ solution: this.explainSolution, question: foundQuestion });
      this.bulkMessage = questionRecord?.message;
    } catch (error) {
      // console.log(error)
    }
  }

  // Function to update the values when the question is changed from the dropdown for ask a teacher/student
  onQuestionChange(selected: any): void {
    if (!selected) return;
    this.bulkMessage = selected?.message || '';
  }

  public openConfirmDeleteDialog(): void {
    try {
      this.spinner.show();
      const dialogRef = this.dialog.open(MatDialogComponent, {
        width: '400px',
        data: {
          title: 'Delete Question',
          icon: '/assets/icons/featured icon.svg',
          accessCodeLabel: 'Delete Question',
          confirmationMessage: 'Are you sure want to delete this question ?',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Delete',
          cancelTrackEvent: 'Cancel Delete Question',
          confirmTrackEvent: 'Delete Question',
          trackPage: 'Question',
        },
      });
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          this.deleteQuestion();
        }
      });
      this.spinner.hide();
    } catch (err) {
      this.spinner.hide();
    }
  }

  public openSubmitDialog(): void {}
  //AI credit Tokens
  async getUserAIToken() {
    try {
      // restrict openAI calls
      this.userAccessAIToken = await this.utilService.getUserAITokens(this.user?.user.id);
    } catch (error) {
      // console.log('LessonPlanComponent getUserToken', error);
    }
  }
  async updateAITokenUsed() {
    try {
      // restrict openAI calls
      await this.utilService.updateUserAITokens(this.user?.user.id, this.userAccessAIToken);

      await this.getUserAIToken();
    } catch (error) {
      // console.log('LessonPlanComponent updateTokenUsed', error);
    }
  }

  ngOnDestroy(): void {
    this.classroomSubscription?.unsubscribe();
    this.extrahelpOptionChangeSubscription?.unsubscribe();
  }

  // Function for reassigning the values
  onQuestionSelect(selected: any): void {
    this.generateQuestionInputValue = selected;
    this.selectedQuestionFromList = selected;
    this.selectedQuestion = selected;
    this.explainSolution = selected?.user_answer || '';
  }

  /**
   * @description Function to check if the question is already asked by the student
   * @param question
   * @returns boolean
   */
  isQuestionAlreadyAsked(question: string): boolean {
    try {
      const isExist = this.studentPreviouslyAskedQuestions.find((q) => q.question === question);
      return isExist ? true : false;
    } catch (error) {
      console.log('students-extra-help.component.ts: isQuestionAlreadyAsked ~ error:', error);
    }
  }
}
